import { useState } from 'react';
import { Plus, Check, X, Pencil, EyeOff, Eye } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { useLogbookTypes } from '../../hooks/useLogbookTypes';
import { colorDeTipo, mensajeErrorNovedad } from '../../utils/logbook';
import { NOTE_TYPE_CODE } from '../../types/logbook';

/**
 * FE-13 — el catálogo de tipos de novedad, solo para el Administrador.
 *
 * Tres reglas del contrato se ven en esta pantalla, y las tres protegen el
 * histórico:
 *
 *  1. **No hay borrado.** Borrar un tipo reescribiría el significado de todas
 *     las novedades que ya lo usan. Se desactiva: sale del formulario y las
 *     viejas siguen diciendo lo que decían.
 *  2. **`code` no se corrige.** Es el eje que los reportes referencian
 *     (ADR-016: el rollup agrupa por `code`). Cambiarlo partiría en dos la
 *     serie histórica de ese tipo.
 *  3. **Desactivar `NOTA` deja al tenant sin novedad libre.** Es el tipo que
 *     sostiene la nota suelta (ADR-015 §33), así que se avisa antes.
 */
export function LogbookTypesPanel({ search }: { search: string }) {
  const { types, isLoading, error, crear, actualizar } = useLogbookTypes();

  const [creando, setCreando] = useState(false);
  const [nuevoCode, setNuevoCode] = useState('');
  const [nuevoLabel, setNuevoLabel] = useState('');
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const visibles = types
    .filter(t =>
      t.label.toLowerCase().includes(search.toLowerCase()) ||
      t.code.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => Number(b.active) - Number(a.active) || a.label.localeCompare(b.label));

  const crearTipo = async () => {
    if (!nuevoCode.trim() || !nuevoLabel.trim()) {
      setAviso('El código y la etiqueta son obligatorios.');
      return;
    }
    setEnCurso(true);
    setAviso(null);
    try {
      await crear({ code: nuevoCode.trim(), label: nuevoLabel.trim() });
      setNuevoCode('');
      setNuevoLabel('');
      setCreando(false);
    } catch (err) {
      setAviso(mensajeErrorNovedad(err, 'catalogo'));
    } finally {
      setEnCurso(false);
    }
  };

  const guardarEtiqueta = async (id: string) => {
    if (!editLabel.trim()) { setAviso('La etiqueta no puede quedar vacía.'); return; }
    setEnCurso(true);
    setAviso(null);
    try {
      await actualizar(id, { label: editLabel.trim() });
      setEditandoId(null);
    } catch (err) {
      setAviso(mensajeErrorNovedad(err, 'catalogo'));
    } finally {
      setEnCurso(false);
    }
  };

  const alternarActivo = async (id: string, code: string, active: boolean) => {
    // Desactivar NOTA es legal en el contrato pero deja al tenant sin dónde
    // escribir notas libres. Se avisa en vez de impedirlo: la decisión es del
    // Administrador, la consecuencia no es evidente.
    if (active && code === NOTE_TYPE_CODE) {
      const seguro = window.confirm(
        'NOTA es el tipo de la novedad libre. Si lo desactivas, nadie podrá registrar notas sueltas. ¿Continuar?',
      );
      if (!seguro) return;
    }
    setEnCurso(true);
    setAviso(null);
    try {
      await actualizar(id, { active: !active });
    } catch (err) {
      setAviso(mensajeErrorNovedad(err, 'catalogo'));
    } finally {
      setEnCurso(false);
    }
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Los tipos se <strong>desactivan</strong>, no se borran: una novedad vieja conserva el suyo
        aunque hoy esté retirado. El <strong>código</strong> no se corrige — es el eje por el que
        agrupan los reportes.
      </p>

      {error && (
        <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444' }}>
          No se pudo cargar el catálogo: {error.message}
        </p>
      )}

      {aviso && (
        <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{aviso}</p>
      )}

      {creando ? (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
          <div style={{ flex: '0 1 180px' }}>
            <Input
              label="Código"
              value={nuevoCode}
              onChange={e => setNuevoCode(e.target.value)}
              placeholder="PINCHAZO"
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <Input
              label="Etiqueta"
              value={nuevoLabel}
              onChange={e => setNuevoLabel(e.target.value)}
              placeholder="Pinchazo"
            />
          </div>
          <Button size="sm" onClick={crearTipo} isLoading={enCurso}>Crear</Button>
          <Button size="sm" variant="ghost" onClick={() => { setCreando(false); setAviso(null); }}>Cancelar</Button>
        </div>
      ) : (
        <Button size="sm" leftIcon={<Plus size={15} />} onClick={() => setCreando(true)} style={{ alignSelf: 'flex-start' }}>
          Nuevo tipo
        </Button>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {visibles.map(t => {
          const color = colorDeTipo(t.code);
          const editando = editandoId === t.id;
          return (
            <li
              key={t.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                background: 'var(--bg-raised)', border: '1px solid var(--border)',
                borderLeft: `3px solid ${t.active ? color : 'var(--border-strong)'}`,
                borderRadius: 'var(--radius-md)', padding: '0.625rem 0.875rem',
                opacity: t.active ? 1 : 0.6,
              }}
            >
              <code style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', minWidth: '120px' }}>
                {t.code}
              </code>

              {editando ? (
                <>
                  <input
                    value={editLabel}
                    onChange={e => setEditLabel(e.target.value)}
                    style={{
                      flex: '1 1 160px', padding: '0.35rem 0.6rem', fontSize: '13px',
                      background: 'var(--bg-surface)', color: 'var(--text-primary)',
                      border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)',
                      fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  <Button size="sm" leftIcon={<Check size={13} />} onClick={() => guardarEtiqueta(t.id)} isLoading={enCurso}>
                    Guardar
                  </Button>
                  <Button size="sm" variant="ghost" leftIcon={<X size={13} />} onClick={() => setEditandoId(null)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {t.label}
                    {!t.active && <span style={{ marginLeft: '0.5rem', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>(inactivo)</span>}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Pencil size={13} />}
                    onClick={() => { setEditandoId(t.id); setEditLabel(t.label); setAviso(null); }}
                  >
                    Renombrar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={t.active ? <EyeOff size={13} /> : <Eye size={13} />}
                    onClick={() => alternarActivo(t.id, t.code, t.active)}
                    disabled={enCurso}
                  >
                    {t.active ? 'Desactivar' : 'Reactivar'}
                  </Button>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {visibles.length === 0 && (
        <p style={{ margin: 0, padding: '1rem', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
          {search ? 'Ningún tipo coincide con la búsqueda.' : 'El catálogo está vacío.'}
        </p>
      )}
    </div>
  );
}
